import React from "react";
import { Tag, Tooltip } from "antd";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { socketMessageToIcon } from "../../../utils/bitmaskToIcon";
import IconDefinition from "../iconDefinition/IconDefinition";
import { ElevatorUsage } from "../../../flatbuffers/generated/usage/elevator-usage";
import { DeviceDirection } from "../../../types/DeviceDirection";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
import { uuidToString } from "../../../utils/uuid";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { msgTypeLabel, resolveValue } from "../../../flatbuffers/decoders/applyDeltaToState";
import { contentTypeDisplayName } from "../../../flatbuffers/types/messageDisplayUtils";

const TAG_COLOR_BY_TYPE: Record<string, string> = {
  FAULT: "volcano",
  TRAFFIC: "gold",
  USAGE: "magenta",
  STATUS: "blue",
};

type TerminalColumnsOptions = {
  terminalShowCarHallCalls: boolean;
  liftNameMap?: Record<string, string>;
  terminalIconMode?: "svg" | "text";
};

export const TerminalTableColumns = ({
  terminalShowCarHallCalls,
  liftNameMap,
  terminalIconMode = "svg",
}: TerminalColumnsOptions) => [
  {
    show: true,
    title: "No",
    key: "no",
    width: 50,
    render: (_: any, __: any, index: number) => <Tag color="gold">{index + 1}</Tag>,
  },
  {
    show: !!liftNameMap && Object.keys(liftNameMap).length > 0,
    title: "Car",
    key: "car_name",
    width: 90,
    dataIndex: "liftId",
    render: (liftId: UuidT | string) => {
      const id = typeof liftId === "string" ? liftId : uuidToString(liftId);
      return <Tag color="purple">{liftNameMap?.[id] ?? id?.substring(0, 8) ?? "-"}</Tag>;
    },
  },
  {
    show: true,
    title: "Icon",
    key: "icon",
    width: 50,
    render: (_: any, row: SocketMessageInterface) => {
      const iconName = socketMessageToIcon(row);
      if (terminalIconMode === "text") {
        if (!iconName) {
          return <Tag color="default">-</Tag>;
        }
        const colorMap: Record<string, string> = {
          elUp: "green",
          elDown: "red",
          elOpening: "gold",
          elRearOpening: "gold",
          elFrontOpeningRearOpening: "gold",
          elFrontOpeningRearOpen: "gold",
          elFrontOpeningRearClose: "gold",
          elFrontOpenRearOpening: "gold",
          elFrontCloseRearOpening: "gold",
          elOpen: "blue",
          elRearOpen: "blue",
          elFrontOpenRearOpen: "blue",
          elClose: "purple",
          elRearClose: "purple",
          elFrontCloseRearClose: "purple",
          elFrontCloseRearOpen: "cyan",
          elFrontOpenRearClose: "cyan",
          elWarning: "orange",
        };
        return <Tag color={colorMap[iconName] ?? "default"}>{iconName.replace(/^el/, "")}</Tag>;
      }
      return (
        <Tooltip title={iconName}>
          <div style={{ width: 24, height: 24 }}>
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={iconName || ""}
              iconSize={20}
            />
          </div>
        </Tooltip>
      );
    },
  },
  {
    show: true,
    title: "Position",
    key: "position",
    width: 70,
    dataIndex: "position",
    render: (value: number) => <Tag color="cyan">{value}</Tag>,
  },
  {
    show: true,
    title: "Msg Active",
    dataIndex: ["msg", "active"],
    key: "msg_active",
    render: (value: boolean) => (
      <>
        {value ? (
          <Tag color="green" className="generalStyles__width100px generalStyles__textCenter">
            True
          </Tag>
        ) : (
          <Tag color="red" className="generalStyles__width100px generalStyles__textCenter">
            False
          </Tag>
        )}
      </>
    ),
  },

  {
    show: true,
    title: "Content Type",
    key: "content_type",
    render: (_: any, row: SocketMessageInterface) => {
      const numeric = row.msg.contentType;
      const label = contentTypeDisplayName(row.msg.contentType);
      return (
        <span>
          <Tag color="orange">{numeric}</Tag>
          <Tag color="default">{label || "-"}</Tag>
        </span>
      );
    },
  },
  {
    show: true,
    title: "Message Type",
    key: "message_type",
    render: (_: any, row: SocketMessageInterface) => {
      const numeric = row.msg.messageType;
      const label = msgTypeLabel(row.msg.contentType, row.msg.messageType);
      return (
        <span>
          <Tag color="blue">{numeric}</Tag>
          <Tag color={TAG_COLOR_BY_TYPE[label.toUpperCase()] || "default"}>{label || "-"}</Tag>
        </span>
      );
    },
  },
  {
    show: true,
    title: "Msg Value",
    key: "msg_value",
    render: (_: any, row: SocketMessageInterface) => resolveValue(row.msg) || "-",
  },
  {
    show: true,
    title: "Direction",
    dataIndex: "direction",
    key: "direction",
    render: (value: DeviceDirection) => {
      const label = directionDisplayName(value);
      let color = "gray";
      if (value === ElevatorUsage.Up) color = "green";
      else if (value === ElevatorUsage.Down) color = "red";
      return (
        <Tag color={color} className="generalStyles__width100px generalStyles__textCenter">
          {label}
        </Tag>
      );
    },
  },
  {
    show: true,
    title: "Door Status Front",
    dataIndex: ["doorStatus", "frontOpen"],
    key: "door_status_front_open",
    render: (text: boolean) => (
      <>
        {text ? (
          <Tag color="green" className="generalStyles__width100px generalStyles__textCenter">
            Open
          </Tag>
        ) : (
          <Tag color="red" className="generalStyles__width100px generalStyles__textCenter">
            Close
          </Tag>
        )}
      </>
    ),
  },
  {
    show: true,
    title: "Door Status Rear",
    dataIndex: ["doorStatus", "rearOpen"],
    key: "door_status_rear_open",
    render: (text: boolean) => (
      <>
        {text ? (
          <Tag color="green" className="terminal__typeTag">
            Open
          </Tag>
        ) : (
          <Tag color="red" className="terminal__typeTag">
            Close
          </Tag>
        )}
      </>
    ),
  },

  {
    show: true,
    title: "Faults",
    dataIndex: "faults",
    key: "faults",
    render: (text: boolean) => (
      <>
        {text ? (
          <Tag color="red" className="generalStyles__width100px generalStyles__textCenter">
            True
          </Tag>
        ) : (
          <Tag color="green" className="generalStyles__width100px generalStyles__textCenter">
            False
          </Tag>
        )}
      </>
    ),
  },
  {
    show: terminalShowCarHallCalls,
    title: "Car Calls",
    dataIndex: "carCalls",
    key: "car_calls",
    render: (text: boolean, row: SocketMessageInterface) => (
      <>
        {row.carCalls.front.map((carCall: number) => (
          <Tag key={`front-${carCall}`} className="terminal__positionTag" color="blue">
            {carCall}
          </Tag>
        ))}
        {row.carCalls.rear.map((carCall: number) => (
          <Tag key={`rear-${carCall}`} className="terminal__positionTag" color="blue">
            {carCall}
          </Tag>
        ))}
      </>
    ),
  },
  {
    show: terminalShowCarHallCalls,
    title: "Lock Car Calls",
    dataIndex: "lock_car_calls",
    key: "lock_car_calls",
    render: (text: boolean, row: SocketMessageInterface) => (
      <>
        {row.carCalls.frontLock.map((carCall: number) => (
          <Tag key={`front-lock-${carCall}`} className="terminal__positionTag" color="orange">
            {carCall}
          </Tag>
        ))}
        {row.carCalls.rearLock.map((carCall: number) => (
          <Tag key={`rear-lock-${carCall}`} className="terminal__positionTag" color="orange">
            {carCall}
          </Tag>
        ))}
      </>
    ),
  },
  {
    show: terminalShowCarHallCalls,
    title: "Hall Calls",
    dataIndex: "hallCalls",
    key: "hall_calls",
    render: (text: boolean, row: SocketMessageInterface) => (
      <>
        {row.hallCalls.up.front.map((hallCall: number) => (
          <Tag key={`up-front-${hallCall}`} className="terminal__positionTag" color="green">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.up.rear.map((hallCall: number) => (
          <Tag key={`up-rear-${hallCall}`} className="terminal__positionTag" color="green">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.down.front.map((hallCall: number) => (
          <Tag key={`down-front-${hallCall}`} className="terminal__positionTag" color="red">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.down.rear.map((hallCall: number) => (
          <Tag key={`down-rear-${hallCall}`} className="terminal__positionTag" color="red">
            {hallCall}
          </Tag>
        ))}
      </>
    ),
  },
  {
    show: terminalShowCarHallCalls,
    title: "Assigned Hall Calls",
    dataIndex: "assigned_hall_calls",
    key: "assigned_hall_calls",
    render: (text: boolean, row: SocketMessageInterface) => (
      <>
        {row.hallCalls.upAssigned.front.map((hallCall: number) => (
          <Tag
            key={`up-assigned-front-${hallCall}`}
            className="terminal__positionTag"
            color="green"
          >
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.upAssigned.rear.map((hallCall: number) => (
          <Tag key={`up-assigned-rear-${hallCall}`} className="terminal__positionTag" color="green">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.downAssigned.front.map((hallCall: number) => (
          <Tag
            key={`down-assigned-front-${hallCall}`}
            className="terminal__positionTag"
            color="red"
          >
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.downAssigned.rear.map((hallCall: number) => (
          <Tag key={`down-assigned-rear-${hallCall}`} className="terminal__positionTag" color="red">
            {hallCall}
          </Tag>
        ))}
      </>
    ),
  },
  {
    show: terminalShowCarHallCalls,
    title: "Lock Hall Calls",
    dataIndex: "lock_hall_calls",
    key: "lock_hall_calls",
    render: (text: boolean, row: SocketMessageInterface) => (
      <>
        {row.hallCalls.up.frontLock.map((hallCall: number) => (
          <Tag key={`up-front-lock-${hallCall}`} className="terminal__positionTag" color="lime">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.up.rearLock.map((hallCall: number) => (
          <Tag key={`up-rear-lock-${hallCall}`} className="terminal__positionTag" color="lime">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.down.frontLock.map((hallCall: number) => (
          <Tag key={`down-front-lock-${hallCall}`} className="terminal__positionTag" color="orange">
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.down.rearLock.map((hallCall: number) => (
          <Tag key={`down-rear-lock-${hallCall}`} className="terminal__positionTag" color="orange">
            {hallCall}
          </Tag>
        ))}
      </>
    ),
  },
  {
    show: terminalShowCarHallCalls,
    title: "Lock Assigned Hall Calls",
    dataIndex: "lock_assigned_hall_calls",
    key: "lock_assigned_hall_calls",
    render: (text: boolean, row: SocketMessageInterface) => (
      <>
        {row.hallCalls.upAssigned.frontLock.map((hallCall: number) => (
          <Tag
            key={`up-assigned-front-lock-${hallCall}`}
            className="terminal__positionTag"
            color="lime"
          >
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.upAssigned.rearLock.map((hallCall: number) => (
          <Tag
            key={`up-assigned-rear-lock-${hallCall}`}
            className="terminal__positionTag"
            color="lime"
          >
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.downAssigned.frontLock.map((hallCall: number) => (
          <Tag
            key={`down-assigned-front-lock-${hallCall}`}
            className="terminal__positionTag"
            color="orange"
          >
            {hallCall}
          </Tag>
        ))}
        {row.hallCalls.downAssigned.rearLock.map((hallCall: number) => (
          <Tag
            key={`down-assigned-rear-lock-${hallCall}`}
            className="terminal__positionTag"
            color="orange"
          >
            {hallCall}
          </Tag>
        ))}
      </>
    ),
  },
];

export default TerminalTableColumns;
